import React, { useState, useEffect, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import ModalForm from '../../../components/modalComponents/ModalForm'
import InputText from '../../../components/modalComponents/InputText'
import Stepper from '../components/Stepper'
import RecapField from '../components/RecapField'
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest'
import { responseCode200, mostrarMensajeDeErrorConToast } from '../../../consumers/httpRequiestsUtils'
import { getDetallePostulantePorEtapasAPI } from '../../../consumers/backendApisUrls'
import { downloadFile } from '../../../consumers/backendConsumer'
import { AuthContext } from '../../../hooks/context/authContext'


// 28/09/2026 (Jody): se oculta "Cierre" del Stepper porque no se muestra nada en
// ese paso. El backend sigue teniendo su propio IdEtapaPostulante para Cierre (uno
// más que la última etapa que sí aparece acá); si un postulante llega a esa etapa,
// etapaBackendAStepUI da un currentStepNumber por encima de la cantidad de círculos
// de este arreglo, así que el Stepper simplemente pinta todos los pasos visibles
// como completados y el cuerpo de abajo no renderiza nada (no hay bloque para ese
// número), que es justamente el comportamiento pedido.
const ETAPAS_POSTULANTE = [
    'Revisión Inicial',
    'Feedback Dirección',
    'Evaluación Académica',
    'Clase Modelo',
    'Entrevista',
    'Asignación Vacante',
];

// TP_EtapaPostulante (backend) -> paso del Stepper de este componente. Ver nota grande
// de arriba. Sin valor de entrada, se asume el paso 1 (Revisión Inicial).
const etapaBackendAStepUI = (idEtapaPostulanteBackend) =>
    idEtapaPostulanteBackend ? idEtapaPostulanteBackend - 1 : 1;

// Preguntas de Feedback Dirección (paso 2), solo lectura: mismas constantes/mapeo que
// FeedbackDireccion.jsx (TP_ConvocatoriaPregunta.TipoPregunta llega crudo 'A'/'C').
const TIPO_ABIERTA = "ABIERTA";
const TIPO_CERRADA = "CERRADA";
const mapTipoPregunta = (tipoPregunta) => tipoPregunta === "C" ? TIPO_CERRADA : TIPO_ABIERTA;

// Normaliza una PreguntasConvocatoriaItemViewModel (PascalCase) a la forma que usa el
// render de abajo. Solo lectura: a diferencia de FeedbackDireccion.jsx no hace falta
// traer los campos de observación por pregunta acá.
const mapPregunta = (pregunta) => ({
    id: pregunta.IdConvocatoriaPregunta,
    tipo: mapTipoPregunta(pregunta.TipoPregunta),
    texto: pregunta.TextoPregunta,
    respuestaTexto: pregunta.RespuestaTexto,
    idAlternativaRespuesta: pregunta.IdAlternativaPreguntaRespuesta,
    alternativas: (pregunta.Alternativas ?? []).map((alternativa) => ({
        id: alternativa.IdAlternativaPregunta,
        texto: alternativa.TextoAlternativa,
    })),
});

// Tabla de Auditoría de Estados, compartida por Revisión Inicial y Feedback Dirección:
// cada paso trae ahora su propia lista (GET_AUDITORIA_ETAPAS_POSTULANTE filtra por
// IdEtapaPostulante), así que solo cambia qué arreglo se le pasa.
const TablaAuditoria = ({ auditoria }) => (
    <table className="table">
        <thead>
            <tr>
                <th>Fecha/Hora</th>
                <th>Etapa</th>
                <th>Estado</th>
                <th>Usuario</th>
            </tr>
        </thead>
        <tbody>
            {auditoria.length === 0 &&
                <tr>
                    <td colSpan={4} className="text-center text-muted">No hay registros de auditoría para este postulante.</td>
                </tr>
            }
            {auditoria.map((cambio) => (
                <tr key={cambio.IdCambiosEstado}>
                    <td>{cambio.FechaRegistro}</td>
                    <td>{cambio.NombreEtapa}</td>
                    <td>{cambio.Estado}</td>
                    <td>{cambio.NombreUsuario}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

// Archivos de la etapa Clase Modelo (DetallePostulanteEtapas4ClaseModeloViewModel.Archivos,
// 30/09/2026): normalmente hasta 2 — "Plantilla GDH" y "Rúbrica del evaluador" (mismos
// nombres ya usados en ResultadoClaseModelo.jsx) — cada uno con su ícono de clip y nombre
// subrayado, clic para descargar. Solo lectura: sin opciones de subir/eliminar, igual que
// LineamientosTrasladoPromocion.jsx con los archivos de apoyo de la convocatoria.
const ArchivosClaseModelo = ({ archivos, onDescargar }) => (
    <div className="mb-3">
        {archivos.length === 0 &&
            <div className="text-muted">No hay archivos registrados para esta etapa.</div>
        }
        {archivos.map((documento, index) => (
            <div key={documento.Discriminator ?? index} className="mb-2">
                <label className="modal-label d-flex mb-1">{documento.NombreDocumento}</label>
                <span className="text-truncate d-inline-block" style={{ maxWidth: "100%" }}
                    role="button" title={documento.File?.OriginalFileName}
                    onClick={() => onDescargar(documento)}>
                    <FontAwesomeIcon icon="paperclip" className="mr-2" />
                    <u>{documento.File?.OriginalFileName}</u>
                </span>
            </div>
        ))}
    </div>
);

const VerDetalleEtapasPostulante = ({ toggleModal, postulante = {} }) => {
    const { state } = useContext(AuthContext);
    const [executeGet] = useGetRequest();
    const [detalle, setDetalle] = useState(null);

    const [currentStepNumber, setCurrentStepNumber] = useState(
        etapaBackendAStepUI(postulante?.IdEtapa ?? postulante?.idEtapa));

    useEffect(() => {
        if (!postulante?.idPostulante) return;
        let cancelado = false;
        (async () => {
            const detalleResp = await executeGet(getDetallePostulantePorEtapasAPI(postulante.idPostulante));
            if (cancelado) return;
            if (responseCode200(detalleResp)) {
                setDetalle(detalleResp.data);
            } else {
                mostrarMensajeDeErrorConToast(detalleResp);
                toggleModal();
            }
        })();
        return () => { cancelado = true; };
    }, [postulante?.idPostulante]);

    // El Stepper solo lee currentStepNumber al montar y cuando cambia (ver
    // Stepper.jsx) — se actualiza acá una vez que llega el detalle real, en vez de
    // esperar a que el usuario haga clic.
    useEffect(() => {
        if (detalle?.IdEtapaPostulante) {
            setCurrentStepNumber(etapaBackendAStepUI(detalle.IdEtapaPostulante));
        }
    }, [detalle]);

    const revisionInicial = detalle?.RevisionInicial ?? {};
    const sedesInteres = revisionInicial.SedesInteres ?? [];
    const auditoriaRevisionInicial = revisionInicial.Auditoria ?? [];
    const declaraciones = revisionInicial.Declaraciones ?? [];

    const feedbackDireccion = detalle?.FeedbackDireccion ?? {};
    const preguntasFeedbackDireccion = (feedbackDireccion.Preguntas ?? []).map(mapPregunta);
    const auditoriaFeedbackDireccion = feedbackDireccion.Auditoria ?? [];

    // Evaluación Académica (paso 3) — nombres provisionales (EvaluacionAcademica /
    // Evaluaciones / Auditoria), a la espera de que Jody confirme el shape real del
    // backend; siguen el mismo patrón que RevisionInicial/FeedbackDireccion de arriba
    // para que después alcance con ajustar nombres de campo si hace falta.
    const evaluacionAcademica = detalle?.EvaluacionAcademica ?? {};
    const evaluaciones = evaluacionAcademica.Evaluaciones ?? [];
    const auditoriaEvaluacionAcademica = evaluacionAcademica.Auditoria ?? [];

    // Clase Modelo (paso 4) — DetallePostulanteEtapas4ClaseModeloViewModel
    // (obtenerDetallePostulantePorEtapas, etapa=5 para su auditoría), 28/09/2026.
    const claseModelo = detalle?.ClaseModelo ?? {};
    const archivosClaseModelo = claseModelo.Archivos ?? [];
    const auditoriaClaseModelo = claseModelo.Auditoria ?? [];

    // Mismo patrón de descarga que LineamientosTrasladoPromocion.jsx / NuevaConvocatoriaMasivo.jsx:
    // consumers/backendConsumer.downloadFile con el token de AuthContext. documento.File viene de
    // DocumentoTyPViewModel (Path/OriginalFileName, igual que ConvocatoriaArchivoApoyoViewModel).
    const handleDescargarArchivoClaseModelo = (documento) => {
        if (!documento?.File?.Path) return;
        downloadFile(state.token, documento.File.Path, documento.File.OriginalFileName);
    };

    // Entrevista (paso 5) — DetallePostulanteEtapas5EntrevistaViewModel: lista de
    // entrevistas programadas/realizadas (Entrevistas, una fila por NroEntrevista)
    // más su propia auditoría (obtenerDetallePostulantePorEtapas, etapa=6), 28/09/2026.
    const entrevista = detalle?.Entrevista ?? {};
    const entrevistas = entrevista.Entrevistas ?? [];
    const auditoriaEntrevista = entrevista.Auditoria ?? [];

    // Asignación Vacante (paso 6) — DetallePostulanteEtapas6AsignacionVacanteViewModel
    // (obtenerDetallePostulantePorEtapas, etapa=7 para su auditoría), 28/09/2026.
    const asignacionVacante = detalle?.AsignacionVacante ?? {};
    const auditoriaAsignacionVacante = asignacionVacante.Auditoria ?? [];

    // Tope de navegación del Stepper: se puede hacer clic en cualquier número de
    // etapa (UI, 1-6 ahora que "Cierre" está oculto — ver nota junto a
    // ETAPAS_POSTULANTE) que sea <= IdEtapaPostulante del backend. IdEtapaPostulante
    // usa su propia numeración (1=Publicación de Convocatoria, 2=Revisión Inicial,
    // 3=Feedback Dirección, ..., 8=Cierre), un paso más que la numeración de este
    // Stepper porque "Publicación de Convocatoria" no tiene paso acá — por eso SÍ hay que convertir con
    // etapaBackendAStepUI (idEtapaPostulanteBackend - 1) antes de comparar, igual
    // que ya se hace para currentStepNumber más abajo (confirmado por Jody,
    // 24/09/2026). maxStepAlcanzado queda fijo mientras no cambie `detalle` (a
    // diferencia de currentStepNumber, que cambia con cada clic) — antes se
    // gateaba el clic contra currentStepNumber, así que al volver a un paso
    // anterior ya no se podía volver a avanzar a uno posterior ya alcanzado.
    //
    // Etapas por las que el postulante YA pasó aunque hoy esté en una anterior: "Retroceder
    // etapa" y "Quitar asignación" lo devuelven a una etapa previa, y si el tope fuera solo la
    // etapa actual el detalle de las que ya transitó (Clase Modelo, Entrevista, Asignación...)
    // dejaba de poder abrirse. La señal es la auditoría de esa etapa (TP_CambiosEstado) o que
    // tenga datos propios. Revisión Inicial siempre cuenta: es el propio postulante.
    const etapasConActividad = [
        true,
        auditoriaFeedbackDireccion.length > 0,
        auditoriaEvaluacionAcademica.length > 0 || evaluaciones.length > 0,
        auditoriaClaseModelo.length > 0 || archivosClaseModelo.length > 0,
        auditoriaEntrevista.length > 0 || entrevistas.length > 0,
        auditoriaAsignacionVacante.length > 0 || !!asignacionVacante.IdRequerimientoPersonal,
    ];
    const ultimoPasoConActividad = etapasConActividad.lastIndexOf(true) + 1;
    const maxStepAlcanzado = Math.min(
        ETAPAS_POSTULANTE.length,
        Math.max(
            etapaBackendAStepUI(detalle?.IdEtapaPostulante ?? postulante?.IdEtapa ?? postulante?.idEtapa),
            ultimoPasoConActividad));

    // Traslado ('T'): las etapas de Promoción (Evaluación Académica, Clase Modelo,
    // Entrevista — pasos 3, 4 y 5) no aplican y quedan deshabilitadas en el Stepper.
    // Promoción ('P') u otro valor: todas las etapas alcanzadas quedan habilitadas.
    const tipoProceso = detalle?.TipoProceso;
    const disabledSteps = tipoProceso === 'T' ? [3, 4, 5] : [];

    // Ayuda al pasar el cursor por cada círculo del Stepper: sin ella no se notaba que las
    // etapas se podían abrir (solo cambiaba el cursor).
    const tituloPaso = (numeroPaso, { canClick, estaDeshabilitado }) => {
        const nombre = ETAPAS_POSTULANTE[numeroPaso - 1];
        if (estaDeshabilitado) return `${nombre}: no aplica a este tipo de proceso`;
        if (canClick) return `Ver el detalle de ${nombre}`;
        return `${nombre}: el postulante aún no llega a esta etapa`;
    };
    // La etapa real del postulante puede ser una sin paso propio (Firma y cierre): ahí no se
    // marca ningún paso y se orienta al usuario en vez de dejar el cuerpo en blanco.
    const enEtapaSinPaso = currentStepNumber > ETAPAS_POSTULANTE.length;

    return (
        <ModalForm onSubmit={(e) => e.preventDefault()} showModal={toggleModal} titulo="Detalle de Etapas del Postulante" hasSaveButton={false}>
            <Stepper steps={ETAPAS_POSTULANTE} currentStepNumber={currentStepNumber} setCurrentStepNumber={setCurrentStepNumber}
                maxStepNumber={maxStepAlcanzado} disabledSteps={disabledSteps} getStepTitle={tituloPaso} />

            <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                Haga clic en una etapa para ver su detalle.
            </div>

            <div className="d-flex flex-wrap" style={{ gap: '24px' }}>
                <RecapField label="Convocatoria" value={detalle?.NombreConvocatoria ?? postulante?.nombreConvocatoria} />
                <RecapField label="Empleado" value={detalle?.NombreEmpleado ?? postulante?.nombrePostulante} />
                <RecapField label="Cargo Actual" value={detalle?.Cargo ?? postulante?.desCargo} />
                <RecapField label="Sede Actual" value={detalle?.Sede ?? postulante?.desSede} />
                <RecapField label="Etapa actual" value={detalle?.NomEtapaPostulante ?? postulante?.desEtapa} />
            </div>

            <hr />

            {enEtapaSinPaso && (
                <div className="text-muted mb-3">
                    El postulante se encuentra en la etapa «{detalle?.NomEtapaPostulante ?? 'Firma y cierre'}».
                    Seleccione una etapa para ver su detalle.
                </div>
            )}

            {currentStepNumber === 1 && (
                <>
                    {tipoProceso==='T' && (
                        <div className="mb-2">
                            <label className="modal-label d-flex">Motivo de traslado</label>
                            <textarea className="form-control" rows={3} disabled value={revisionInicial?.MotivoTraslado ?? ''} onChange={() => {}} />
                        </div>
                    )}
                    {revisionInicial?.MotivoNoApto && (
                        <div className="mb-2">
                            <label className="modal-label d-flex">Motivo de No apto</label>
                            <textarea className="form-control" rows={3} disabled value={revisionInicial.MotivoNoApto} onChange={() => {}} />
                        </div>
                    )}
                    
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Curso / nivel que dicta actualmente" name="especialidadActual" value={revisionInicial?.EspecialidadActual} direction="column" isDisabled /></div>
                    </div>

                    <div className="row">
                        <div className="col-sm-6"><InputText label="Sede de interés #1" name="sedeInteres1" value={sedesInteres[0]?.DesSede} direction="column" isDisabled /></div>
                        <div className="col-sm-6"><InputText label="Sede de interés #2" name="sedeInteres2" value={sedesInteres[1]?.DesSede} direction="column" isDisabled /></div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Sede de interés #3" name="sedeInteres3" value={sedesInteres[2]?.DesSede} direction="column" isDisabled /></div>
                    </div>

                    <hr />

                    <label className="mb-2 col-sm-12 mt-2"><strong>Declaraciones Aceptadas:</strong></label>
                    <ul className="list-unstyled mb-3">
                        {declaraciones.length === 0 &&
                            <li className="text-muted">No hay declaraciones registradas para este postulante.</li>
                        }
                        {declaraciones.map((declaracion) => (
                            <li key={declaracion.IdPostulanteDeclaracion} className="d-flex align-items-start mb-2">
                                <FontAwesomeIcon icon="check-circle" className="text-success mr-2 mt-1" title="Declaración aceptada" />
                                <span>{declaracion.TextoAceptado}</span>
                            </li>
                        ))}
                    </ul>

                    <label className="mb-3 col-sm-12 mt-2"><strong>Auditoría de Estados:</strong></label>
                    <TablaAuditoria auditoria={auditoriaRevisionInicial} />
                </>
            )}

            {currentStepNumber === 2 && (
                <>
                    <label className="mb-3 col-sm-12"><strong>Preguntas</strong></label>
                    <PreguntasBox>
                        {preguntasFeedbackDireccion.length === 0 &&
                            <div className="text-muted">No hay preguntas registradas para esta convocatoria.</div>
                        }
                        {preguntasFeedbackDireccion.map((pregunta, index) => (
                            <div className="pregunta-item" key={pregunta.id}>
                                <label className="modal-label d-flex">{`#${index + 1} ${pregunta.texto}`}</label>
                                {pregunta.tipo === TIPO_ABIERTA
                                    ? (
                                        <InputText name={`pregunta-${pregunta.id}`} value={pregunta.respuestaTexto ?? ""} isDisabled />
                                    )
                                    : (
                                        <AlternativaToggle>
                                            {pregunta.alternativas.map((alternativa) => (
                                                <button type="button" key={alternativa.id} disabled
                                                    className={pregunta.idAlternativaRespuesta === alternativa.id ? "active" : ""}>
                                                    {alternativa.texto}
                                                </button>
                                            ))}
                                        </AlternativaToggle>
                                    )
                                }
                            </div>
                        ))}
                    </PreguntasBox>

                    <hr />

                    <label className="mb-3 col-sm-12 mt-2"><strong>Auditoría de Estados:</strong></label>
                    <TablaAuditoria auditoria={auditoriaFeedbackDireccion} />
                </>
            )}

            {currentStepNumber === 3 && (
                <>
                    <label className="mb-3 col-sm-12"><strong>Evaluaciones</strong></label>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nombre Evaluación</th>
                                <th>Tipo Evaluación</th>
                                <th>Fec. Inicio</th>
                                <th>Fec. Finalización</th>
                                <th>Puntaje Obtenido</th>
                            </tr>
                        </thead>
                        <tbody>
                            {evaluaciones.length === 0 &&
                                <tr>
                                    <td colSpan={5} className="text-center text-muted">No hay evaluaciones registradas para este postulante.</td>
                                </tr>
                            }
                            {evaluaciones.map((evaluacion, index) => (
                                <tr key={evaluacion.IdEvaluacion ?? index}>
                                    <td>{evaluacion.NombreEvaluacion}</td>
                                    <td>{evaluacion.TipoEvaluacion}</td>
                                    <td>{evaluacion.FechaInicio}</td>
                                    <td>{evaluacion.FechaFinalizacion}</td>
                                    <td>{evaluacion.PuntajeObtenido}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <hr />

                    <label className="mb-3 col-sm-12 mt-2"><strong>Auditoría de Estados:</strong></label>
                    <TablaAuditoria auditoria={auditoriaEvaluacionAcademica} />
                </>
            )}

            {currentStepNumber === 4 && (
                <>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Estado etapa" name="estadoEtapaClaseModelo" value={claseModelo?.EstadoPostulanteEtapa} direction="column" isDisabled /></div>
                        <div className="col-sm-6"></div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Evaluador" name="evaluadorClaseModelo" value={claseModelo?.NombreEvaluador} direction="column" isDisabled /></div>
                        <div className="col-sm-6"><InputText label="Sede de Evaluación" name="sedeEvaluacionClaseModelo" value={claseModelo?.Sede} direction="column" isDisabled /></div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Fecha límite" name="fechaLimiteClaseModelo" value={claseModelo?.FechaLimite} direction="column" isDisabled /></div>
                    </div>

                    <div className="mb-2 mt-2">
                        <label className="modal-label d-flex">Comentario de evaluador</label>
                        <textarea className="form-control" rows={3} disabled value={claseModelo?.ComentarioEvaluador ?? ''} onChange={() => {}} />
                    </div>

                    <div className="row">
                        <div className="col-sm-12"><InputText label="Motivo observación" name="motivoObservadoClaseModelo" value={claseModelo?.MotivoObservado} direction="column" isDisabled /></div>
                    </div>

                    <hr />

                    <label className="mb-3 col-sm-12 mt-2"><strong>Archivos:</strong></label>
                    <ArchivosClaseModelo archivos={archivosClaseModelo} onDescargar={handleDescargarArchivoClaseModelo} />

                    <hr />

                    <label className="mb-3 col-sm-12 mt-2"><strong>Auditoría de Estados:</strong></label>
                    <TablaAuditoria auditoria={auditoriaClaseModelo} />
                </>
            )}

            {currentStepNumber === 5 && (
                <>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Estado etapa" name="estadoEtapaEntrevista" value={entrevista?.EstadoPostulanteEtapa} direction="column" isDisabled /></div>
                        <div className="col-sm-6"></div>
                    </div>

                    <table className="table">
                        <thead>
                            <tr>
                                <th>Fecha/Hora</th>
                                <th>Entrevistador</th>
                                <th>Lugar reunión</th>
                                <th>Resumen</th>
                                <th>Estado</th>
                                <th>Fecha resultado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entrevistas.length === 0 &&
                                <tr>
                                    <td colSpan={6} className="text-center text-muted">No hay entrevistas registradas para este postulante.</td>
                                </tr>
                            }
                            {entrevistas.map((fila, index) => (
                                <tr key={fila.IdNroEntrevista ?? index}>
                                    <td>{fila.FechaHora}</td>
                                    <td>{fila.NombreEmpleadoEntrevistador}</td>
                                    <td>{fila.LugarReunion}</td>
                                    <td>{fila.ResumenEntrevista}</td>
                                    <td>{fila.EstadoEntrevista}</td>
                                    <td>{fila.FechaResultado}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <hr />

                    <label className="mb-3 col-sm-12 mt-2"><strong>Auditoría de Estados:</strong></label>
                    <TablaAuditoria auditoria={auditoriaEntrevista} />
                </>
            )}

            {currentStepNumber === 6 && (
                <>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Id. Vacante" name="idVacanteAsignacion" value={asignacionVacante?.IdRequerimientoPersonal} direction="column" isDisabled /></div>
                        <div className="col-sm-6"><InputText label="Cargo Promoción" name="cargoPromocionAsignacion" value={asignacionVacante?.CargoPromocion} direction="column" isDisabled /></div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Nuevo Sueldo" name="nuevoSueldoAsignacion" value={asignacionVacante?.NuevoSueldo} direction="column" isDisabled /></div>
                        <div className="col-sm-6"><InputText label="Sede Traslado" name="sedeTrasladoAsignacion" value={asignacionVacante?.SedeTraslado} direction="column" isDisabled /></div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Fecha ejecución" name="fechaEjecucionAsignacion" value={asignacionVacante?.FechaEjecucion} direction="column" isDisabled /></div>
                        <div className="col-sm-6"><InputText label="Motivo Traslado" name="motivoTrasladoAsignacion" value={asignacionVacante?.MotivoTraslado} direction="column" isDisabled /></div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6"><InputText label="Estado" name="estadoAsignacion" value={asignacionVacante?.Estado} direction="column" isDisabled /></div>
                    </div>

                    <hr />

                    <label className="mb-3 col-sm-12 mt-2"><strong>Auditoría de Estados:</strong></label>
                    <TablaAuditoria auditoria={auditoriaAsignacionVacante} />
                </>
            )}
        </ModalForm>
    )
}

export default VerDetalleEtapasPostulante

// Caja con borde redondeado que envuelve la sección "Preguntas" (mismo estilo que
// PreguntasBox de FeedbackDireccion.jsx).
const PreguntasBox = styled.div`
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;

    .pregunta-item {
        margin-bottom: 20px;
    }

    .pregunta-item:last-child {
        margin-bottom: 0;
    }
`;

// Grupo de alternativas tipo "pill" para preguntas CERRADA, resaltando la elegida —
// copiado tal cual de FeedbackDireccion.jsx (acá siempre disabled, es solo lectura).
// 29/09/2026: mismo fix que el original — píldoras independientes con `gap` en vez
// de un borde compartido + separadores, que se veían superpuestos al pasar a 2+
// filas con alternativas largas.
const AlternativaToggle = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    button {
        border: 1px solid #3a87c8;
        border-radius: 15px;
        outline: none !important;
        background-color: #ffffff;
        color: #3a87c8;
        padding: 4px 20px;
        font-family: "GothamLight", system-ui, sans-serif;
        flex-shrink: 0;
    }

    button.active {
        background-color: #3a87c8;
        color: #ffffff;
    }

    button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
